import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { AdminUser } from "../interfaces/adminAuth.interface";
import { adminLogin, adminSignup, adminLogout, adminGetProfile, adminUpdateProfile, adminUploadProfileImage } from "../thunks/adminAuth.thunk";
import { API_CONFIG } from "../../../utils/apiConfig";

interface AdminAuthState {
  admin: AdminUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
}

function getStoredAdmin(): AdminUser | null {
  try {
    const raw = localStorage.getItem(API_CONFIG.ADMIN_USER_STORAGE_KEY);
    if (!raw) return null;
    const token = localStorage.getItem(API_CONFIG.ADMIN_TOKEN_STORAGE_KEY);
    if (!token) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function hasValidAdminToken(): boolean {
  const token = localStorage.getItem(API_CONFIG.ADMIN_TOKEN_STORAGE_KEY);
  if (!token) return false;
  try {
    const payload = JSON.parse(atob(token.split(".")[1]));
    return payload.exp > Date.now() / 1000;
  } catch {
    return false;
  }
}

const storedAdmin = getStoredAdmin();
const isTokenValid = hasValidAdminToken();

const initialState: AdminAuthState = {
  admin: isTokenValid ? storedAdmin : null,
  isAuthenticated: isTokenValid && storedAdmin !== null,
  isLoading: false,
  error: null,
};

const adminAuthSlice = createSlice({
  name: "adminAuth",
  initialState,
  reducers: {
    clearAdminError: (state) => {
      state.error = null;
    },
    adminLogoutLocal: (state) => {
      state.admin = null;
      state.isAuthenticated = false;
      state.isLoading = false;
      state.error = null;
      localStorage.removeItem(API_CONFIG.ADMIN_TOKEN_STORAGE_KEY);
      localStorage.removeItem(API_CONFIG.ADMIN_REFRESH_TOKEN_KEY);
      localStorage.removeItem(API_CONFIG.ADMIN_USER_STORAGE_KEY);
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(adminLogin.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(adminLogin.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isAuthenticated = true;
        state.admin = action.payload.admin ?? null;
        state.error = null;
      })
      .addCase(adminLogin.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      .addCase(adminSignup.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(adminSignup.fulfilled, (state) => {
        state.isLoading = false;
        state.error = null;
      })
      .addCase(adminSignup.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      .addCase(adminLogout.fulfilled, (state) => {
        state.admin = null;
        state.isAuthenticated = false;
        state.isLoading = false;
        state.error = null;
      })
      .addCase(adminGetProfile.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(adminGetProfile.fulfilled, (state, action) => {
        state.isLoading = false;
        state.admin = action.payload;
        state.isAuthenticated = true;
      })
      .addCase(adminGetProfile.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      .addCase(adminUpdateProfile.fulfilled, (state, action) => {
        state.admin = action.payload;
        state.error = null;
      })
      .addCase(adminUpdateProfile.rejected, (state, action) => {
        state.error = action.payload as string;
      })
      .addCase(adminUploadProfileImage.fulfilled, (state, action) => {
        if (state.admin) {
          state.admin = { ...state.admin, profileImage: action.payload.profileImage };
        }
        state.error = null;
      })
      .addCase(adminUploadProfileImage.rejected, (state, action) => {
        state.error = action.payload as string;
      });
  },
});

export const { clearAdminError, adminLogoutLocal } = adminAuthSlice.actions;
export default adminAuthSlice.reducer;
